import { useEffect, useRef } from 'react';

const NEWSLETTER_ROOT_SELECTOR = '[data-newsletter-widget], [component="newsletterWidget"]';

const getNewsletterRoots = (root: ParentNode): Element[] => {
    if (root instanceof Element && root.matches(NEWSLETTER_ROOT_SELECTOR)) {
        return [root];
    }

    if (root instanceof Document || root instanceof Element || root instanceof DocumentFragment) {
        return Array.from(root.querySelectorAll(NEWSLETTER_ROOT_SELECTOR));
    }

    return [];
};

const fixNewsletterFormAccessibility = (root: ParentNode = document): void => {
    const newsletterRoots = getNewsletterRoots(root);

    newsletterRoots.forEach(widgetRoot => {
        widgetRoot.querySelectorAll('img.onFormSubmittedFeedbackIcon').forEach(icon => {
            if (icon instanceof HTMLImageElement) {
                icon.setAttribute('aria-hidden', 'true');
            }
        });

        widgetRoot.querySelectorAll('[id^="editorIdPrefix"]').forEach(element => {
            if (!(element instanceof HTMLElement)) {
                return;
            }

            if (!element.hasAttribute('role')) {
                element.setAttribute('role', 'textbox');
            }

            element.removeAttribute('aria-expanded');

            if (element.getAttribute('aria-activedescendant') === '') {
                element.removeAttribute('aria-activedescendant');
            }
        });

        widgetRoot.querySelectorAll('table').forEach(table => {
            table.setAttribute('role', 'presentation');
        });
    });
};

/**
 * NewsletterWidget has several events that can be listened to.
 *
 * @see {@link https://learn.microsoft.com/en-us/dynamics365/customer-insights/journeys/developer/realtime-marketing-form-client-side-extensibility#custom-events}
 */
const Events = () => {
    const widgetsRef = useRef<NodeListOf<Element> | null>(null);

    useEffect(() => {
        const handleFormRender = () => {
            const widgets = document.querySelectorAll('[component="newsletterWidget"]');

            widgetsRef.current = widgets;

            widgets.forEach((widget, i) => {
                const form = widget.querySelector('form');

                if (form) {
                    form.id = `newsletter-widget-${i}`;
                }
            });

            fixNewsletterFormAccessibility(document);
        };

        const handleFormSubmit = () => {
            requestAnimationFrame(() => {
                fixNewsletterFormAccessibility(document);
            });
        };

        document.addEventListener('d365mkt-afterformload', handleFormRender);
        document.addEventListener('d365mkt-afterformsubmit', handleFormSubmit);

        return () => {
            document.removeEventListener('d365mkt-afterformload', handleFormRender);
            document.removeEventListener('d365mkt-afterformsubmit', handleFormSubmit);
        };
    }, []);

    return null;
};

export default Events;
